import { useParams } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { useTenantsQuery } from '@/entities/tenant'
import { useSetTicketTourFeaturesMutation, useTicketByIdQuery } from '@/entities/ticket'
import { useTicketFeaturesQuery } from '@/entities/ticket-feature'
import { DEFAULT_PAGE_SIZE } from '@/shared/constants/pagination'
import { useDashboardDisplayLanguage } from '@/shared/hooks/use-dashboard-display-language'
import { useScopedAdminPath } from '@/shared/hooks/use-scoped-admin-path'
import { getApiErrorText } from '@/shared/libs/get-api-error-text'
import { showToast } from '@/shared/libs/toast'
import { EmptyState } from '@/shared/ui/empty-state'
import { PageContainer } from '@/shared/ui/page-container'
import { PageHeader } from '@/shared/ui/page-header'
import { Skeleton } from '@/shared/ui/skeleton'
import { StatusBadge } from '@/shared/ui/status-badge'
import { TicketTourFeaturesCard } from '@/features/ticket/ticket-tour-features-card'

export function TicketDetailPage() {
  const { t } = useTranslation()
  const scopedAdminPath = useScopedAdminPath()
  const { activeLanguage } = useDashboardDisplayLanguage()
  const { id }: { id?: string } = useParams({ strict: false })

  const { data: ticketResponse, isLoading } = useTicketByIdQuery(id)
  const { data: featuresResponse } = useTicketFeaturesQuery({ current: 1, pageSize: 100 })
  const { data: tenantsResponse } = useTenantsQuery({
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    isActive: true,
  })
  const mutation = useSetTicketTourFeaturesMutation()

  const ticket = ticketResponse?.data
  const features = featuresResponse?.data.data ?? []
  const tenantName = tenantsResponse?.data.find((tenant) => tenant.id === ticket?.tenantId)?.name

  async function handleSave(tourId: string, featureIds: string[]) {
    if (!ticket) return
    try {
      await mutation.mutateAsync({ ticketId: ticket.id, tourId, payload: { featureIds } })
      showToast.success(t('ticket.saveSuccess', { defaultValue: 'Ticket tour features updated' }))
    } catch (error) {
      showToast.error(getApiErrorText(error) ?? t('common.operationFailed'))
    }
  }

  return (
    <PageContainer>
      <PageHeader
        title={ticket?.orderItemId ?? t('ticket.detailTitle', { defaultValue: 'Ticket detail' })}
        breadcrumbs={[
          {
            label: t('ticket.title', { defaultValue: 'Tickets' }),
            path: scopedAdminPath('/tickets'),
          },
          { label: ticket?.orderItemId ?? id ?? '' },
        ]}
      />

      {isLoading && <Skeleton className="h-40 w-full" />}

      {ticket && (
        <>
          <dl className="mb-6 grid grid-cols-2 gap-4 rounded-xl border border-zinc-200 bg-white p-4 text-sm md:grid-cols-4">
            <div>
              <dt className="text-xs text-zinc-500">{t('sidebar.tenants')}</dt>
              <dd className="font-medium">{tenantName ?? ticket.tenantId}</dd>
            </div>
            <div>
              <dt className="text-xs text-zinc-500">
                {t('ticketConfig.ticketType', { defaultValue: 'Type' })}
              </dt>
              <dd className="font-medium">
                {t(`ticketConfig.${ticket.ticketType}`, { defaultValue: ticket.ticketType })}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-zinc-500">{t('common.status')}</dt>
              <dd>
                <StatusBadge
                  status={ticket.status}
                  label={t(`ticket.status.${ticket.status}`, { defaultValue: ticket.status })}
                />
              </dd>
            </div>
            <div>
              <dt className="text-xs text-zinc-500">{t('common.createdAt')}</dt>
              <dd className="font-medium">{new Date(ticket.createdAt).toLocaleDateString()}</dd>
            </div>
          </dl>

          {ticket.tours.length === 0 ? (
            <EmptyState
              title={t('ticket.noTours', { defaultValue: 'This ticket has no tours' })}
              description=""
            />
          ) : (
            <div className="space-y-4">
              {ticket.tours.map((tour) => (
                <TicketTourFeaturesCard
                  key={`${tour.tourId}:${tour.features.map((feature) => feature.featureId).join(',')}`}
                  tour={tour}
                  features={features}
                  languageCode={activeLanguage}
                  saving={mutation.isPending}
                  onSave={(featureIds) => void handleSave(tour.tourId, featureIds)}
                />
              ))}
            </div>
          )}
        </>
      )}
    </PageContainer>
  )
}
